import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Plus, Pencil, Upload, Trash2, ImageOff, Percent, ChevronDown, ChevronUp, Search, Printer } from 'lucide-react';
import { printContent } from '../../../utils/print';

const formatCurrency = v => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

export default function ProdutosLista() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const printRef = useRef(null);

  // Painel ajuste de preço
  const [showAjuste, setShowAjuste] = useState(false);
  const [ajusteCats, setAjusteCats] = useState([]); // ids selecionadas
  const [ajusteTodasCats, setAjusteTodasCats] = useState(true);
  const [ajustePerc, setAjustePerc] = useState('');
  const [ajusteLoading, setAjusteLoading] = useState(false);
  const [ajusteResult, setAjusteResult] = useState(null);

  const loadProdutos = () => {
    setLoading(true);
    const params = filter !== '' ? `?is_ativo=${filter}` : '';
    api.get(`/produtos${params}`)
      .then(r => setProdutos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProdutos(); }, [filter]);
  useEffect(() => {
    api.get('/categorias').then(r => setCategorias(r.data)).catch(() => {});
  }, []);

  const filtered = produtos.filter(p =>
    p.descricao?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/produtos/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadProdutos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir produto');
    } finally { setDeleting(false); }
  };

  const handleCategoriaChange = async (produto, novaCategoriaId) => {
    try {
      await api.put(`/produtos/${produto.id}`, {
        sku: produto.sku || '', descricao: produto.descricao || '',
        categoria_id: novaCategoriaId, tipo_fermentacao: produto.tipo_fermentacao || '',
        tempo_fermentacao: produto.tempo_fermentacao || '', unidade_medida: produto.unidade_medida || 'Unidade',
        pedido_minimo: produto.pedido_minimo || 1, prazo_entrega: produto.prazo_entrega || '48h',
        imagem_url: produto.imagem_url || '', ncm: produto.ncm || '',
        codigo_barras: produto.codigo_barras || '', peso_bruto: produto.peso_bruto || 0,
        peso_liquido: produto.peso_liquido || 0, estoque_minimo: produto.estoque_minimo || 0,
        estoque_maximo: produto.estoque_maximo || 0, cest: produto.cest || '',
        origem: produto.origem || 0, is_ativo: produto.is_ativo !== undefined ? produto.is_ativo : true,
      });
      loadProdutos();
    } catch { alert('Erro ao salvar categoria'); }
  };

  const toggleCat = (id) => setAjusteCats(cs => cs.includes(id) ? cs.filter(c => c !== id) : [...cs, id]);

  const handleAjuste = async () => {
    const perc = parseFloat(ajustePerc);
    if (isNaN(perc) || perc === 0) { alert('Informe um percentual válido (ex: 10 ou -5)'); return; }
    if (!window.confirm(`Aplicar ${perc > 0 ? '+' : ''}${perc}% nos preços ${ajusteTodasCats ? 'de TODAS as categorias' : `de ${ajusteCats.length} categoria(s)`}? Esta ação não pode ser desfeita.`)) return;
    setAjusteLoading(true);
    setAjusteResult(null);
    try {
      const res = await api.post('/produtos/ajuste-preco', {
        categoria_ids: ajusteTodasCats ? [] : ajusteCats,
        percentual: perc,
      });
      setAjusteResult(res.data);
      loadProdutos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao aplicar ajuste');
    } finally { setAjusteLoading(false); }
  };

  const inputCls = "rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-primary/30";

  return (
    <div data-testid="produtos-lista">
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-dashboard font-bold text-lg text-brand-text mb-2">Confirmar exclusão</h3>
            <p className="text-sm font-body text-brand-text-muted mb-1">Excluir permanentemente o produto <strong className="text-brand-text">"{deleteTarget.label}"</strong>?</p>
            <p className="text-xs text-amber-600 font-body mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 rounded-lg border border-brand-border text-sm font-dashboard text-brand-text-muted" data-testid="cancel-delete-btn">Cancelar</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-dashboard font-semibold disabled:opacity-60" data-testid="confirm-delete-btn">{deleting ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="dashboard-heading text-2xl">Produtos</h1>
          <p className="text-brand-text-muted font-body text-sm">{filtered.length} produto(s)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => printContent('Produtos', printRef)}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 py-2 px-4 rounded-lg border border-brand-border font-dashboard font-semibold text-sm hover:bg-brand-bg transition-colors disabled:opacity-50"
            data-testid="print-produtos-btn"
          >
            <Printer className="w-4 h-4" /> Imprimir
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => { setShowAjuste(v => !v); setAjusteResult(null); }}
                className="flex items-center gap-2 py-2 px-4 rounded-lg border border-brand-border font-dashboard text-sm hover:bg-brand-bg"
                data-testid="ajuste-preco-btn"
              >
                <Percent className="w-4 h-4" /> Ajuste de Preço
                {showAjuste ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              <Link to="/admin/produtos/importar" className="flex items-center gap-2 py-2 px-4 rounded-lg border border-brand-border font-dashboard text-sm hover:bg-brand-bg" data-testid="import-btn">
                <Upload className="w-4 h-4" /> Importar XLSX
              </Link>
            </>
          )}
          <Link to="/admin/produtos/novo" className="flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="new-product-btn">
            <Plus className="w-4 h-4" /> Novo Produto
          </Link>
        </div>
      </div>

      {/* Painel Ajuste de Preço em % */}
      {showAjuste && isAdmin && (
        <div className="card-dashboard mb-4 border-l-4 border-brand-primary" data-testid="ajuste-preco-panel">
          <h3 className="font-dashboard font-semibold text-brand-text mb-4 flex items-center gap-2">
            <Percent className="w-4 h-4 text-brand-primary" /> Ajuste de Preço em Massa
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-brand-text-muted mb-2 font-body font-medium">Categorias</label>
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input type="checkbox" checked={ajusteTodasCats} onChange={e => { setAjusteTodasCats(e.target.checked); setAjusteCats([]); }} data-testid="todas-cats-check" />
                <span className="text-sm font-body font-medium text-brand-text">Todas as categorias</span>
              </label>
              {!ajusteTodasCats && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto border border-brand-border rounded-lg p-2">
                  {categorias.map(c => (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-brand-bg rounded px-1.5 py-1">
                      <input type="checkbox" checked={ajusteCats.includes(c.id)} onChange={() => toggleCat(c.id)} data-testid={`cat-check-${c.id}`} />
                      <span className="text-sm font-body text-brand-text">{c.nome}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-2 font-body font-medium">Percentual de Ajuste</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.1"
                  placeholder="Ex: 10 ou -5"
                  className={`${inputCls} w-36`}
                  value={ajustePerc}
                  onChange={e => { setAjustePerc(e.target.value); setAjusteResult(null); }}
                  data-testid="ajuste-perc-input"
                />
                <span className="text-brand-text font-dashboard font-bold text-lg">%</span>
              </div>
              {ajustePerc && !isNaN(parseFloat(ajustePerc)) && (
                <p className="text-xs text-brand-text-muted font-body mt-1.5">
                  {parseFloat(ajustePerc) > 0 ? `Aumento de ${ajustePerc}%` : `Redução de ${Math.abs(parseFloat(ajustePerc))}%`}
                  {' '}em {ajusteTodasCats ? 'todas as categorias' : `${ajusteCats.length} categoria(s) selecionada(s)`}
                </p>
              )}
              {ajusteResult && (
                <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2 mt-2 font-body">
                  Ajuste aplicado! {ajusteResult.updated} preço(s) atualizado(s) em {ajusteResult.produtos_afetados} produto(s).
                </p>
              )}
              <button
                onClick={handleAjuste}
                disabled={ajusteLoading || !ajustePerc || (!ajusteTodasCats && ajusteCats.length === 0)}
                className="mt-3 px-5 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-50"
                data-testid="aplicar-ajuste-btn"
              >
                {ajusteLoading ? 'Aplicando...' : 'Aplicar Ajuste'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card-dashboard mb-4 flex gap-3">
        <input className={`flex-1 ${inputCls}`} placeholder="Buscar produto ou SKU..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-produtos" />
        <select className={inputCls} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">Todos</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
      </div>

      <div className="card-dashboard overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm py-12 text-brand-text-muted font-body">Nenhum produto encontrado</p>
        ) : (
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="border-b border-brand-border bg-brand-bg">
                <th className="py-2.5 px-2 w-10"></th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">SKU</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Descrição</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Categoria</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Unidade</th>
                <th className="text-right py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Preço</th>
                <th className="py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-brand-border last:border-0 hover:bg-brand-bg/50">
                  <td className="py-2 px-2">
                    {p.imagem_url ? (
                      <img src={p.imagem_url} alt={p.descricao} className="w-9 h-9 rounded-lg object-cover border border-brand-border" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    ) : null}
                    <div className={`w-9 h-9 rounded-lg bg-brand-accent-light flex items-center justify-center ${p.imagem_url ? 'hidden' : 'flex'}`}>
                      <ImageOff className="w-4 h-4 text-brand-text-muted/40" />
                    </div>
                  </td>
                  <td className="py-2.5 px-3 font-mono text-xs text-brand-text-muted">{p.sku}</td>
                  <td className="py-2.5 px-3 font-medium text-brand-text">{p.descricao}</td>
                  <td className="py-2.5 px-3">
                    {isAdmin ? (
                      <select className="rounded border border-brand-border px-1.5 py-1 text-xs font-body bg-white focus:outline-none focus:ring-1 focus:ring-brand-primary" value={p.categoria_id || ''} onChange={e => handleCategoriaChange(p, e.target.value)} data-testid="categoria-inline-select">
                        <option value="">—</option>
                        {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </select>
                    ) : (
                      <span className="text-brand-text-muted">{p.categoria_nome || '–'}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-brand-text-muted">{p.unidade_medida}</td>
                  <td className="py-2.5 px-3 text-right font-medium text-brand-text">
                    {formatCurrency(p.preco_exibicao ?? p.preco_unitario)}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-3">
                      <Link to={`/admin/produtos/${p.id}`} className="p-1.5 rounded hover:bg-brand-accent-light text-brand-primary hover:text-brand-dark transition-colors" title="Editar" data-testid="edit-product-btn">
                        <Pencil className="w-3.5 h-3.5" />
                      </Link>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.is_ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.is_ativo ? 'Ativo' : 'Inativo'}</span>
                      {isAdmin && (
                        <button onClick={() => setDeleteTarget({ id: p.id, label: p.descricao })} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Excluir" data-testid="delete-produto-btn">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tabela somente leitura, usada apenas para impressão (printContent lê este ref) */}
      <div ref={printRef} className="hidden">
        <div className="mb-4 pb-3 border-b-2 border-brand-dark">
          <h2 className="font-dashboard font-bold text-xl text-brand-text">Fábrica de Pães</h2>
          <p className="text-sm font-body text-brand-text-muted">Lista de Produtos</p>
          <p className="text-xs font-body text-brand-text-muted">
            Gerado em {new Date().toLocaleDateString('pt-BR')} · {filtered.length} produto(s)
          </p>
        </div>
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="border-b-2 border-brand-dark">
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">SKU</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Descrição</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Categoria</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Unidade</th>
              <th className="text-right py-1.5 px-2 text-xs font-dashboard">Preço</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className="border-b border-brand-border">
                <td className="py-1.5 px-2 font-mono text-xs">{p.sku}</td>
                <td className="py-1.5 px-2 font-semibold text-brand-text">{p.descricao}</td>
                <td className="py-1.5 px-2">{p.categoria_nome || '–'}</td>
                <td className="py-1.5 px-2">{p.unidade_medida}</td>
                <td className="py-1.5 px-2 text-right">{formatCurrency(p.preco_exibicao ?? p.preco_unitario)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

